/* global Cookies, gtag */
import brightcovePlayerLoader from '@brightcove/player-loader';
import * as Sentry from '@sentry/browser';

import { UserStore } from 'Main/UserStore';
import VideoCountdown from 'Main/VideoCountdown';
import { EVENT_PLAYER_CHAPTER_CHANGE } from 'Utils/types';

import PixelTracker from '../tracking/PixelTracker';
import createCustomComponents from './customVideoComponents';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

export class BrightcovePlayer {
    constructor() {
        this._lastReportTime = 0;
        this._reportTimeInterval = 20000; // variable, depending on the playback rate
        this._lastPlayerProgressSeconds = -1;
        this._customControls = document.querySelector('.js_videoCustomControls');
        this._tapToUnmuteButton = document.querySelector('.js_tapToUnmute');
        this._seekLabelTimeout = null;
        this._seekControlsTimeout = null;
        this._lastSeekDirection = null;
        this._seekCounter = 0;
        this._currentTime = null;
        this._currentTimeInterval = null;
        this._isTouchDevice = 'ontouchstart' in window;

        const nextVideoContext = window.__NEXT_VIDEO_CONTEXT__ || {};
        this._countdown = new VideoCountdown({
            nextVideoAPIUrl: nextVideoContext.nextVideoAPIUrl,
            initialTime: 0,
            videoCountdownCompletedUrl: nextVideoContext.videoCountdownCompletedUrl,
        });
        this.pixelTracker = this._setupTrackingPixel();
    }

    async initialize(
        playerElement,
        brightcoveId,
        accountId,
        playerId,
        autoplay = context.autoplay,
        prerollServerUrl = context.prerollServerUrl,
    ) {
        /*
            autoplay scenarios:
            direct page visit - autoplay: true; muted: true;
            domain navigation - autoplay: true; muted: false;
            same page refresh - autoplay: true; muted: true;
        */
        const navigationEntries = performance.getEntriesByType('navigation');
        const pageReloaded = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
        let muted = pageReloaded || !!autoplay;

        if (!pageReloaded && document.referrer) {
            const referrerUrl = new URL(document.referrer);

            if (referrerUrl.hostname === window.location.hostname) {
                // same domain referrer
                muted = false;
            }
        }

        this._player =
            playerElement &&
            (await brightcovePlayerLoader({
                refNode: playerElement,
                refNodeInsert: brightcovePlayerLoader.REF_NODE_INSERT_REPLACE,
                accountId,
                playerId,
                embedId: 'default',
                videoId: brightcoveId,
                embedOptions: {
                    pip: true,
                },
                options: {
                    // Videojs options
                    controls: true,
                    preload: 'auto',
                    fluid: true,
                    autoplay,
                    muted,
                    playsinline: true,
                    controlBar: {
                        remainingTimeDisplay: false,
                    },
                },
            }).then((success) => success.ref));

        this._player.ima3.settings = {
            ...this._player.ima3.settings,
            debug: false,
            serverUrl: prerollServerUrl || '',
            timeout: 5000,
            requestMode: 'onload',
            debugContribAds: false,
            disableCustomPlaybackForIOS10Plus: true,
            showVpaidControls: true,
            vpaidMode: 'DISABLED',
        };

        createCustomComponents(this);

        this._player.on('loadedmetadata', () => {
            // if the `t` query parameter is added, start the video from the value set for the param
            const videoDuration = this._player.duration();
            const queryTimestamp = context.timestamp;

            if (queryTimestamp && queryTimestamp < videoDuration) {
                this._player.currentTime(queryTimestamp);
            }
        });

        this._player.on('loadeddata', () => {
            // move the current timestamp inside the thumbnail preview element
            const timestampElement = document.querySelector('.vjs-time-tooltip');
            const thumbnailElement = document.querySelector('.vjs-thumbnail-display');
            thumbnailElement?.append(timestampElement);
        });

        this._player.on('pipactive', () => {
            const isiPreview = document.querySelector('.js_floatingISIPreview');
            const videoContainer = this._player.el_;

            if (!isiPreview || !videoContainer) return;

            const isiPreviewHeight = isiPreview.clientHeight;
            videoContainer.style.bottom = `${isiPreviewHeight + 14}px`;
        });

        this._player.on('pipinactive', () => {
            const videoContainer = this._player.el_;

            if (!videoContainer) return;

            videoContainer.removeAttribute('style');
        });

        this._player.on('play', () => {
            const muteButton = document.querySelector('.vjs-mute-control');

            if (this._player.muted()) {
                muteButton.classList.add('vjs-vol-0');
            } else {
                muteButton.classList.remove('vjs-vol-0');
            }
        });

        if (this._player) {
            this._onPlayerReadyHandlers(this._player);
            this._fixForPipFullScreen(this._player);
            this._player.controlBar.playbackRateMenuButton.handleClick = () => {}; // disable default behavior
        }

        this._videoControlsHandler();

        this._videoChaptersControls(this._player);

        this._currentTimeInterval = setInterval(() => {
            if (!this._player) {
                clearInterval(this._currentTimeInterval);
                return;
            }
            this._currentTime = this._player.currentTime();
        }, 1000);

        // Expose player
        window.player = this._player;
        return this._player;
    }

    /**
     * Sourced from https://stackoverflow.com/a/9039885/2711654
     * Note that `navigator.platform` is deprecated. Its replacement is `navigator.userAgentData`
     * but that's not yet properly supported. That's why the deprecated variant is still used.
     *
     * @returns {boolean|boolean} True if estimated that the client is running on iOS
     */
    _isRunningOnIOS() {
        return (
            ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
                navigator.platform,
            ) ||
            // iPad on iOS 13 detection
            (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
        );
    }

    _chaptersToggleHandler = (e, eventTrigger = 'Chapters Icon') => {
        if (!this.chaptersContainer) return;

        e.stopPropagation();

        this.chaptersContainer.classList.toggle('is-expanded');

        const videoTimestampMiliseconds = Math.floor(this._currentTime * 1000);
        const chaptersStatus = this.chaptersContainer.classList.contains('is-expanded') ? 'Shown' : 'Hidden';

        window.FS?.event(`${eventTrigger} ${chaptersStatus}`, {
            viewed_uuid: context.viewedUUID,
            video_timestamp: videoTimestampMiliseconds,
        });

        window.eventTracker.trackChapterListToggle(videoTimestampMiliseconds, chaptersStatus, eventTrigger);
    };

    _onPlayerReadyHandlers(player) {
        this._handlePlayPermission(player);

        if (context?.updateViewedTimeUrl && context.viewedUUID) {
            this._handlePrerollEventsTracking(player);
            this._handlePlayerTimeTracking(player);
            this._handlePlayerFullscreenTracking(player);
            this._handleOrientationTracking();
        }

        // User settings
        player.defaultPlaybackRate(this._loadUserPlaybackRate());
        player.volume(this._loadUserVolumeLevel());
    }

    _remove_pip_style_attribute() {
        const pipElement = document.getElementsByClassName('vjs-pip-container')[0];
        if (!pipElement) return;
        pipElement.removeAttribute('style');
    }

    _fixForPipFullScreen(player) {
        // Picture in picture breaks full screen fix!
        // PIP plugins dynamically set the width/height style attribute to vjs-pip-container and don't change it back
        // after exiting full screen so this will reset those values and plugin will automatically populate it again.
        player.on('fullscreenchange', () => {
            this._remove_pip_style_attribute();
        });
        window.addEventListener('resize', () => {
            this._remove_pip_style_attribute();
        });
    }

    _setupTrackingPixel() {
        const pixelContext = window.__TRACKING_PIXEL_CONTEXT__;
        if (!pixelContext) return null;

        const container = document.querySelector('main.container');
        if (!container) return null;

        return new PixelTracker({ pixelContext, container });
    }

    _setReportingTime(playerProgressSeconds, now, chapterId = undefined) {
        this._lastReportTime = now;
        this._lastPlayerProgressSeconds = Math.max(this._lastPlayerProgressSeconds, playerProgressSeconds);
        if (this.hasChapters && chapterId !== undefined) {
            this._lastPlayerProgressPerChapterSeconds[chapterId] = Math.max(
                this._lastPlayerProgressPerChapterSeconds[chapterId],
                playerProgressSeconds,
            );
        }
    }

    _checkAndReportPlayedTime(playerProgressSeconds, forceUpdate = false, seek = false, useFetchMethod = false) {
        const now = new Date().getTime();
        const playerProgressMs = playerProgressSeconds * 1000;

        let chapterId;
        let lastChapterProgressSeconds = Number.MAX_SAFE_INTEGER; // if no chapters then min will pick video progress
        if (this.hasChapters) {
            chapterId = this._findActiveChapterForCurrentTime(playerProgressMs).dataset.chapterId;
            lastChapterProgressSeconds = this._lastPlayerProgressPerChapterSeconds[chapterId];
        }
        const lastPlayerProgressSeconds = Math.min(lastChapterProgressSeconds, this._lastPlayerProgressSeconds);

        if (playerProgressSeconds < lastPlayerProgressSeconds) return;
        if (
            now - this._lastReportTime > this._reportTimeInterval / this._player.playbackRate() ||
            (seek && playerProgressSeconds - lastPlayerProgressSeconds > 10) || // Prevent reporting short seeks
            forceUpdate
        ) {
            this._setReportingTime(playerProgressSeconds, now, chapterId);

            const postJson = JSON.stringify({ viewed_time: playerProgressMs });
            const headers = {
                'X-CSRFToken': Cookies.get('csrftoken') || context.csrftoken,
            };
            let setViewedTimeRequest;

            const ajaxViewedTime = (options) =>
                new Promise((resolve, reject) => {
                    $.ajax({
                        ...options,
                        success: resolve,
                        error: reject,
                    });
                });

            if (useFetchMethod) {
                setViewedTimeRequest = fetch(context?.updateViewedTimeUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                        ...headers,
                    },
                    body: postJson,
                    keepalive: true,
                });
            } else {
                setViewedTimeRequest = ajaxViewedTime({
                    url: context?.updateViewedTimeUrl,
                    dataType: 'json',
                    contentType: 'application/json; charset=utf-8',
                    data: postJson,
                    timeout: 10000,
                    type: 'PUT',
                    headers,
                });
            }

            setViewedTimeRequest
                .then(async (response) => {
                    if (useFetchMethod && !response?.ok) {
                        // if fetch() is used, we need to throw an error
                        let errorDetail;

                        try {
                            const json = await response.json();
                            errorDetail = json.detail || JSON.stringify(json);
                        } catch (parseError) {
                            const rawResponse = await response.text();
                            const message = parseError.message || rawResponse;
                            const status = `Status: ${response?.status || 'unknown'}`;
                            errorDetail = `Failed to parse fetch() error response: ${message}. ${status}`;
                        }

                        throw new Error(`fetch() error: ${errorDetail}`);
                    }

                    console.log(`Reporting viewed time ${playerProgressMs}`);
                })
                .catch((err) => {
                    if (err.statusText === 'timeout' || err.name === 'AbortError') {
                        Sentry.withScope((scope) => {
                            scope.setFingerprint(['[TIMEOUT] Set viewed time']);
                            Sentry.captureException(
                                new Error(
                                    `The request has timed out. Data: ${postJson}, Headers: ${JSON.stringify(
                                        headers,
                                    )}, viewed UUID: ${context.viewedUUID}, Err: ${JSON.stringify(err)}.`,
                                ),
                            );
                        });
                    } else {
                        const errorMessage = useFetchMethod
                            ? err.message || 'Unknown fetch error'
                            : err.responseText || JSON.stringify(err);

                        Sentry.withScope((scope) => {
                            scope.setFingerprint(['Set viewed time error']);
                            Sentry.captureException(
                                new Error(
                                    `Something went wrong with reporting viewed_time. Data: ${postJson}, Headers: ${JSON.stringify(
                                        headers,
                                    )},viewed UUID: ${context.viewedUUID},  Err: ${errorMessage}.`,
                                ),
                            );
                        });
                    }
                });
        }
    }

    _getCurrentAdData() {
        const { ima3 } = this._player;

        try {
            for (const value of Object.values(ima3.currentAd)) {
                if ('adWrapperIds' in value) {
                    return value;
                }
            }
            throw new Error('Missing adWrapperIds in values.');
        } catch (err) {
            Sentry.withScope((scope) => {
                scope.setFingerprint(['Preroll ad data error']);
                Sentry.captureException(
                    new Error(
                        `Can not get current ad data from ima3. ima3: ${JSON.stringify(ima3)}, Err: ${JSON.stringify(
                            err,
                        )}.`,
                    ),
                );
            });
        }

        return null;
    }

    _setGoogleAdData() {
        if (!this.lineItemId) {
            const currentAd = this._getCurrentAdData();
            if (currentAd.adWrapperCreativeIds.length) {
                [this.lineItemId] = currentAd.adWrapperIds;
                [this.creativeId] = currentAd.adWrapperCreativeIds;
            } else {
                this.lineItemTitle = currentAd.title;
                this.lineItemId = currentAd.adId;
                this.creativeId = currentAd.creativeId;
            }

            if (Number.isNaN(this.lineItemId % 1) || Number.isNaN(this.creativeId % 1)) {
                Sentry.withScope((scope) => {
                    scope.setFingerprint(['Preroll ad data error']);
                    scope.addAttachment({ filename: 'currentAd.txt', data: JSON.stringify(currentAd) });
                    Sentry.captureException(
                        new Error(`Invalid line item ${this.lineItemId} or creative ${this.creativeId}.`),
                    );
                    scope.clearAttachments();
                });
            }
        }
    }

    _report_perroll_viewed_time(skipped = false, clicked = false, finished = false) {
        const { ad } = this._player.ads;
        let viewedTime = ad.currentTime || 1;

        if (finished) {
            viewedTime = ad.duration;
        }

        this._setGoogleAdData();

        const postData = JSON.stringify({
            viewed_uuid: context.viewedUUID,
            line_item_title: this.lineItemTitle,
            line_item_id: this.lineItemId,
            creative_id: this.creativeId,
            viewed_time: `${viewedTime * 1000}`,
            skipped,
            clicked,
            finished,
        });
        const headers = {
            'X-CSRFToken': Cookies.get('csrftoken') || context.csrftoken,
        };
        $.ajax({
            url: context.prerollAnalyticsUrl,
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            data: postData,
            type: 'POST',
            headers,
        })
            .then(() => {
                console.log(`Reporting preroll viewed time ${viewedTime * 1000}`, skipped, clicked, finished);
            })
            .catch((err) => {
                Sentry.withScope((scope) => {
                    scope.setFingerprint(['Set preroll viewed time error']);
                    Sentry.captureException(
                        new Error(
                            `Something went wrong with reporting preroll viewed_time. Data: ${postData}, Err: ${JSON.stringify(
                                err,
                            )}.`,
                        ),
                    );
                });
            });
    }

    _showSafetyInformation() {
        this._setGoogleAdData();
        $.ajax({
            url: context.videoSafetyInformationUrl,
            data: {
                line_item_id: this.lineItemId,
                creative_id: this.creativeId,
            },
        }).success((data) => {
            $('#video-safety-information-box').html(data);
        });
    }

    _hideSafetyInformation() {
        $('#video-safety-information-box').html('');
    }

    _handlePlayPermission(player) {
        if (!context.playPermission) {
            player.el_.classList.add('vjs-login-required');
            player.on('play', () => {
                player.pause();
                $('#loginOrRegisterModal').modal('show');
            });
        }
    }

    /**
     * Using this.lineItemTitle, this.lineItemId, and this.creativeId properties.
     */
    _handlePrerollEventsTracking(player) {
        // [PY-2386] Viewed time tracking patch for autoplay videos
        player.on('adserror', (e) => {
            if (context.autoplay) {
                e.target.player.trigger('contentresumed');
            }
        });
        player.on('ads-ad-started', () => {
            this._showSafetyInformation();
            this._report_perroll_viewed_time();
        });
        player.on('ads-ad-ended', () => {
            this._report_perroll_viewed_time(false, false, true);
            this._hideSafetyInformation();
        });
        player.on('ads-ad-skipped', () => {
            this._report_perroll_viewed_time(true);
            this._hideSafetyInformation();
        });
        player.on('ads-click', () => {
            this._report_perroll_viewed_time(false, true);
        });

        // Report banner viewed time
        player.on('ads-first-quartile', () => {
            this._report_perroll_viewed_time();
        });
        player.on('ads-midpoint', () => {
            this._report_perroll_viewed_time();
        });
        player.on('ads-third-quartile', () => {
            this._report_perroll_viewed_time();
        });
        window.addEventListener('beforeunload', () => {
            if (this.ads) {
                this._report_perroll_viewed_time();
            }
        });
    }

    _handlePlayerTimeTracking(player) {
        this._checkAndReportPlayedTime(0, true); // Sets viewed time to 0

        // Player handlers
        player.one('play', (e) => {
            const reportTime = e.target.player.currentTime();
            this._checkAndReportPlayedTime(reportTime || 1, true); // Sometimes reportTime can be 0.
            this.pixelTracker?.trackInitialPlay();
        });
        player.on('ended', (e) => {
            this._checkAndReportPlayedTime(e.target.player.duration(), true);

            window.FS?.event('Video Ended', { viewed_uuid: context.viewedUUID });

            window.redirectEnabled = true;
            const productWorkshopContext = window.__PRODUCT_WORKSHOP_CONTEXT__;
            // If productWorkshopContext is set countdown will be handled in product_workshop.js file.
            if (productWorkshopContext) {
                if (productWorkshopContext.activityPreTest && !productWorkshopContext.preTestSubmission) {
                    $('#pre-test-modal').modal('show');
                } else if (productWorkshopContext.activityPostTest && !productWorkshopContext.postTestSubmission) {
                    $('#post-test-modal').modal('show');
                } else {
                    // If all tests solved only then run countdown at the end of a video.
                    this._countdown.run();
                }
            } else {
                this._countdown.run();
            }
        });
        player.on('playing', () => {
            this._countdown.hide();
        });
        player.on('seeked', (e) => {
            const eventPlayer = e.target.player;
            const reportTime = eventPlayer.currentTime();

            this._checkAndReportPlayedTime(reportTime, false, true);
            this.pixelTracker?.update(reportTime, eventPlayer.duration());

            // sanity check for seek parameter
            if (!reportTime || reportTime <= this._lastPlayerProgressSeconds) return;
            // const seekedAmount = (reportTime - this._lastPlayerProgressSeconds); TODO
            gtag('event', 'seek_forward_time', { event_category: 'Video', event_label: context?.videoId });
        });
        player.on('timeupdate', (e) => {
            // If manuallyTriggered is true that means someone clicked on the scrubber (this is reported in seeked event).
            const manuallyTriggered = !!e.manuallyTriggered;
            if (!manuallyTriggered) {
                const eventPlayer = e.target.player;
                this._checkAndReportPlayedTime(eventPlayer.currentTime());
                this.pixelTracker?.update(eventPlayer.currentTime(), eventPlayer.duration());
            }
        });
        window.addEventListener('beforeunload', () => {
            const reportTime = player.currentTime();

            if (reportTime) {
                this._checkAndReportPlayedTime(reportTime, true, false, true);
            }
        });
    }

    _handlePlayerFullscreenTracking(player) {
        player.on('fullscreenchange', () => {
            if (player.isFullscreen()) {
                window.eventTracker.trackFullscreenOn();
            } else {
                window.eventTracker.trackFullscreenOff();
            }
        });
    }

    _handleOrientationTracking() {
        // Track only playable videos
        if (!context.playPermission) {
            return;
        }
        const portrait = window.matchMedia('(orientation: portrait)');
        // Track initial orientation
        if (portrait.matches) {
            window.eventTracker.trackOrientationPortrait();
        } else {
            window.eventTracker.trackOrientationLandscape();
        }
        // Track consecutive orientations
        portrait.addEventListener('change', (e) => {
            if (e.matches) {
                window.eventTracker.trackOrientationPortrait();
            } else {
                window.eventTracker.trackOrientationLandscape();
            }
        });
    }

    _loadUserPlaybackRate() {
        return UserStore.loadPlaybackRate() || 1;
    }

    _saveUserPlaybackRate(playbackRate) {
        UserStore.savePlaybackRate(playbackRate);
    }

    _loadUserVolumeLevel() {
        const savedVolumeLevel = UserStore.loadVolumeLevel();

        return savedVolumeLevel === undefined ? 1 : savedVolumeLevel;
    }

    _saveUserVolumeLevel(level) {
        UserStore.saveVolumeLevel(level);
    }

    _handleVolumeChange() {
        const volume = this._player.muted() ? 0 : this._player.volume();

        if (volume === 0) {
            this._player.muted(true);
        }

        this._saveUserVolumeLevel(volume);
    }

    _findActiveChapterForCurrentTime(playerProgressMs) {
        return this.chapterItems.find((item) => {
            const { timestampStart, timestampEnd } = item.dataset;
            return playerProgressMs >= timestampStart && playerProgressMs < timestampEnd;
        });
    }

    _handlePlayerRateChange() {
        const currentPlaybackRate = this._player.playbackRate();
        this._saveUserPlaybackRate(currentPlaybackRate);
        this._checkAndReportPlayedTime(this._player.currentTime(), true); // report and reset tracking
        this._trackPlaybackRateChange(currentPlaybackRate);
    }

    _videoChaptersControls(player) {
        // TODO: some of the variables below should probably be class properties.
        this.chaptersContainer = document.querySelector('.js_chaptersContainer');
        this.hasChapters = !!this.chaptersContainer;

        if (!this.hasChapters) return;

        const videoElement = document.querySelector('video-js');
        const chaptersToggleBtn = document.createElement('button');
        const chaptersList = this.chaptersContainer.querySelector('.js_chaptersList');
        const closeButton = this.chaptersContainer.querySelector('.js_chaptersCloseButton');
        this.chapterItems = Array.from(this.chaptersContainer.querySelectorAll('.js_chapterItem'));

        // player progess in here is not relative to chapter
        this._lastPlayerProgressPerChapterSeconds = {};
        for (const chapterItem of this.chapterItems) {
            const { chapterId, timestampStart } = chapterItem.dataset;
            this._lastPlayerProgressPerChapterSeconds[chapterId] = timestampStart / 1000;
        }

        /**
         * Click listener that is called whenever a chapter is changed, caused by following:
         *
         * - Initial playout of the video, i.e. initialization of chapters
         * - Video progress naturally enters into new chapter.
         * - User clicks inside a progress bar somewhere inside non-active chapter.
         * - User clicks on a chapter inside chapter list.
         */
        const chapterClickHandler = (item, shouldSkipToChapter = true) => {
            const { timestampStart, chapterId, chapterOrdinal, chapterTitle } = item.dataset;

            window.dispatchEvent(new CustomEvent(EVENT_PLAYER_CHAPTER_CHANGE, { detail: { chapterId } }));
            document.querySelector('.vjs-custom-metadata-chapter-title span').innerText = chapterTitle;

            // scroll the user back to the video
            if (window.innerWidth < 768 && shouldSkipToChapter) {
                setTimeout(() => {
                    videoElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                }, 100);
            }

            if (item.classList?.contains('is-active')) return;

            chaptersList.querySelector('.js_chapterItem.is-active')?.classList.remove('is-active');
            item.classList.add('is-active');

            if (shouldSkipToChapter) {
                const videoTimestampMiliseconds = Math.floor(this._currentTime * 1000);

                window.FS?.event('Chapter Clicked', {
                    viewed_uuid: context.viewedUUID,
                    chapter_timestamp: timestampStart,
                    chapter_id: chapterOrdinal,
                    video_id: context.videoId,
                    chapter_title: chapterTitle,
                });
                window.eventTracker.trackChapterClick(
                    videoTimestampMiliseconds,
                    chapterOrdinal,
                    chapterTitle,
                    timestampStart,
                );
                player.currentTime(timestampStart / 1000);
            }
        };

        /**
         * Listener that automatically changes the active chapter based on the video progress.
         *
         * Listener is called when progress naturally enters into new chapter, or when a user clicks
         * inside a progress bar somewhere inside non-active chapter.
         *
         * This listener is not called when a user clicks a chapter inside chapter list.
         */
        const triggerChapterChangeBasedOnCurrentPlayheadTime = (currentTime) => {
            const chapterForTimestamp = this._findActiveChapterForCurrentTime(currentTime);
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');

            if (chapterForTimestamp && chapterForTimestamp !== activeChapter) {
                chapterClickHandler(chapterForTimestamp, false);
            }
        };

        this.chaptersContainer.classList.add('is-expanded');

        closeButton.addEventListener('click', () => {
            const videoTimestampMiliseconds = Math.floor(this._currentTime * 1000);

            window.FS?.event('Chapter Hide Button Clicked', {
                viewed_uuid: context.viewedUUID,
                video_timestamp: this._currentTime * 1000,
            });

            window.eventTracker.trackChapterListToggle(videoTimestampMiliseconds, 'Hidden', 'Hide Button');

            chaptersToggleBtn.classList.toggle('is-active');
            this.chaptersContainer.classList.toggle('is-expanded');
        });

        player.on('seeked', () => {
            const currentTime = player.currentTime() * 1000;
            triggerChapterChangeBasedOnCurrentPlayheadTime(currentTime);
        });

        let lastExecutionTime = 0;
        let thumbnailPreviewMoved = false;

        player.on('timeupdate', () => {
            const currentTime = player.currentTime();
            if (lastExecutionTime === 0 || currentTime - lastExecutionTime >= 10 || lastExecutionTime > currentTime) {
                lastExecutionTime = currentTime;
                triggerChapterChangeBasedOnCurrentPlayheadTime(currentTime * 1000);
            }

            if (!thumbnailPreviewMoved) {
                thumbnailPreviewMoved = true;
                const thumbnailPreview = document.querySelector('.vjs-thumbnail-display');
                const thumbnailPreviewParent = thumbnailPreview?.parentNode;
                thumbnailPreviewParent?.insertAdjacentElement('afterend', thumbnailPreview);
            }
        });

        player.one('loadedmetadata', () => {
            const progresControlElement = player.controlBar.progressControl.el();
            const highlightProgressElement = document.createElement('div');
            highlightProgressElement.classList.add('video_chapters__progress_bar', 'js_chapterProgressBar');
            const progressSlider = document.querySelector('.vjs-progress-holder.vjs-slider.vjs-slider-horizontal');
            progresControlElement.append(highlightProgressElement);
            let clipPathString = '0 0, ';

            this.chapterItems.forEach((item, index) => {
                const videoDuration = player.duration() * 1000; // Convert to ms

                const { timestampStart, timestampEnd } = item.dataset;
                const markerStartingPosition = `${(timestampStart / videoDuration) * 100}%`;

                if (parseInt(markerStartingPosition, 10) > 0) {
                    clipPathString += `${markerStartingPosition} 0%, ${markerStartingPosition} 100%, calc(${markerStartingPosition} + 4px) 100%, calc(${markerStartingPosition} + 4px) 0%, `;
                    if (index + 1 === this.chapterItems.length) {
                        clipPathString += '100% 0%, 100% 100%, 0% 100%';
                    }
                }

                progressSlider.style.setProperty('--progress-holder-polygon-coordinates', clipPathString);

                item.addEventListener('click', () => chapterClickHandler(item));

                item.addEventListener('mouseenter', () => {
                    const startPercentage = (timestampStart / videoDuration) * 100;
                    const endPercentage = (timestampEnd / videoDuration) * 100;

                    Object.assign(highlightProgressElement.style, {
                        left: `calc(${startPercentage}% + 4px)`,
                        width: `calc(${endPercentage - startPercentage}% - 4px)`,
                    });

                    highlightProgressElement.classList.add('visible');
                });

                item.addEventListener('mouseleave', () => {
                    highlightProgressElement.classList.remove('visible');
                });
            });

            // Set initial chapter
            chapterClickHandler(this.chapterItems[0], false);
        });
    }

    _videoControlsHandler() {
        const fullscreenToggleButton = this._player.getDescendant(['ControlBar', 'FullscreenToggle']);
        const loginRequired = this._player?.el_.classList.contains('vjs-login-required');
        const captionsButton = this._player.controlBar.getChild('SubsCapsButton')?.el_;
        const { tracks_: captionTracks } = this._player.textTracks();
        const activeTrack = captionTracks.find(
            (track) => ['captions', 'subtitles'].includes(track.kind) && track.mode === 'showing',
        );
        let captionsShown = false;

        if (activeTrack) {
            // not sure is it possible or not, but
            // in case there is an active track by default
            // add the active class to the captionsButton
            captionsButton.classList.toggle('is-active');
        }

        const toggleCaptions = () => {
            captionsButton.classList.toggle('is-active');
            captionsShown = !captionsShown;
            captionTracks.forEach((track) => {
                if (['captions', 'subtitles'].includes(track.kind)) {
                    track.mode = captionsShown ? 'showing' : 'disabled'; // Use 'hidden' to hide without disabling
                    window.FS?.event('Captions toggled', {
                        viewed_uuid: context.viewedUUID,
                        value: captionsShown ? 'showing' : 'disabled',
                    });
                }
            });
        };

        captionsButton.addEventListener('click', toggleCaptions);
        captionsButton.addEventListener('touchend', toggleCaptions);

        // blur out the fullscreen button to enable keyboard seeking
        fullscreenToggleButton?.on('click', () => {
            fullscreenToggleButton.blur();
        });

        document.addEventListener('keydown', (event) => {
            if (loginRequired) return;

            if (event.key === 'ArrowRight') {
                this._seekVideo('forward', 'key');
            } else if (event.key === 'ArrowLeft') {
                this._seekVideo('backward', 'key');
            }
        });

        this._player.controlBar?.progressControl?.on('mousedown', () => {
            this._seekbarHandler();
        });

        this._player.controlBar?.progressControl?.seekBar?.on('mousedown', () => {
            this._seekbarHandler();
        });

        this._player.on('ratechange', () => {
            this._handlePlayerRateChange();
        });
    }

    _trackPlaybackRateChange(playbackRate) {
        const eventTimestampMiliseconds = Math.floor(this._currentTime * 1000);
        window.eventTracker.trackPlaybackRateChange(playbackRate, eventTimestampMiliseconds);
        gtag('event', 'playback_rate_changed', {
            event_category: 'Video',
            event_label: context?.videoId,
            playback_rate: playbackRate,
            video_timestamp: eventTimestampMiliseconds,
        });
        window.FS?.event('Playback Rate Changed', {
            viewed_uuid: context.viewedUUID,
            playback_rate: playbackRate,
            video_timestamp: eventTimestampMiliseconds,
        });
    }

    _seekbarHandler() {
        const seekedTime = this._player.currentTime();
        const direction = seekedTime > this._currentTime ? 'forward' : 'backward';
        this._seekVideo(direction, 'scrubber');
    }

    _startControlsTimeout(eventName, customControlsWrap) {
        // the seek controls will dissapear after 3 seconds unless another action (seek forward/backward) is triggered
        clearTimeout(this._seekControlsTimeout);
        this._seekControlsTimeout = setTimeout(() => {
            if (['mouseover', 'touchend', 'click'].includes(eventName)) {
                customControlsWrap?.classList.add('disable-hover-styles');
            }
            customControlsWrap?.classList.remove('show-seek-controls');
            setTimeout(() => {
                this._player.userActive(false);
            }, 1);
        }, 3000);
    }

    _seekVideo(direction, source) {
        const eventTimestampMiliseconds = Math.floor(this._currentTime * 1000);
        const currentTime = this._player.currentTime();
        const duration = Math.floor(this._player.duration());
        const remainingTime = duration - this._currentTime;
        const directionTitleCase = direction.charAt(0).toUpperCase() + direction.slice(1);
        const sourceTitleCase = source.charAt(0).toUpperCase() + source.slice(1);

        window.FS?.event('Video Seek Triggered', {
            viewed_uuid: context.viewedUUID,
            video_timestamp: eventTimestampMiliseconds,
            direction: directionTitleCase,
            source: sourceTitleCase,
        });

        gtag('event', 'video_seek_triggered', {
            event_category: 'Video',
            event_label: context?.videoId,
            video_timestamp: eventTimestampMiliseconds,
            direction: directionTitleCase,
            source: sourceTitleCase,
        });

        window.eventTracker.trackVideoSeek(sourceTitleCase, directionTitleCase, eventTimestampMiliseconds);

        if (!['button', 'key'].includes(source)) return;

        this._startControlsTimeout();

        if (direction === 'forward') {
            if (remainingTime >= 10) {
                this._player.currentTime(currentTime + 10);
                this._showSeekLabel('forward');
            } else {
                this._player.currentTime(duration);
            }
        } else if (currentTime >= 10) {
            this._player.currentTime(currentTime - 10);
            this._showSeekLabel('backward');
        } else {
            this._player.currentTime(0);
        }
    }

    _showSeekLabel(direction) {
        const seekLabel = document.querySelector('.js_videoSeekLabel');
        clearTimeout(this._seekLabelTimeout);

        if (direction !== this._lastSeekDirection) {
            this._seekCounter = 0;
        }

        this._lastSeekDirection = direction;
        seekLabel?.classList.remove('playing-forward', 'playing-backward');
        this._seekCounter += direction === 'forward' ? 1 : -1;
        seekLabel?.classList.add(`playing-${direction}`, 'is-visible');

        const labelParagraph = seekLabel?.querySelector('p');
        if (labelParagraph) {
            labelParagraph.innerText = `${Math.abs(this._seekCounter * 10)} seconds`;
        }

        this._seekLabelTimeout = setTimeout(() => {
            seekLabel?.classList.remove('is-visible');
            this._seekCounter = 0;
            this._lastSeekDirection = null;
        }, 2000);
    }
}

export class EmbeddedBrightcovePlayer extends BrightcovePlayer {
    _onPlayerReadyHandlers() {}
}
